import { useMutation, useQueryClient } from '@tanstack/react-query';
import { orderReceiptService } from '@/services/orderReceiptService';
import { queryKeys } from '@/lib/queryKeys';
import { useNotifications } from '@/stores/useNotificationStore';
import { getErrorMessage } from '@/utils/httpClient';
import type { OrderListRow } from '@/services/ordersService';

export interface CompleteOrderReceiptRequest {
  orderId: string;
  items: Array<{ lineItemId: string; quantity: number; locationId?: string | null }>;
  timeoutMs?: number;
}

export const useCompleteOrderReceipt = () => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  return useMutation({
    mutationFn: async (request: CompleteOrderReceiptRequest) => {
      return await orderReceiptService.completeOrderReceipt(
        {
          orderId: request.orderId,
          items: request.items,
        },
        request.timeoutMs ? { timeout: request.timeoutMs } : undefined,
      );
    },
    onSuccess: (data, variables) => {
      // Eagerly patch the order status in the cache so the UI reflects the
      // new status immediately, avoiding a temporal mismatch between the
      // audit trail (which polls every 5 s) and the stale order row.
      queryClient.setQueryData(
        queryKeys.orders.list(),
        (oldData: OrderListRow[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.map((order) =>
            order.id === variables.orderId
              ? {
                  ...order,
                  entity_data: {
                    ...order.entity_data,
                    status: data.orderStatus,
                  },
                }
              : order,
          );
        },
      );

      void queryClient.invalidateQueries({ queryKey: queryKeys.orders.all });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.orders.lineItems(variables.orderId),
      });

      const receiptCount = data.receiptIds.length;
      const assetCount = data.createdAssetIds.length;

      if (receiptCount === 0 && assetCount === 0) {
        notifications.info(
          'Order Already Received',
          `All line items were previously received. Order status: ${data.orderStatus}`,
        );
      } else {
        notifications.success(
          'Order Received',
          `Created ${receiptCount} receipts and ${assetCount} assets. Order status: ${data.orderStatus}`,
        );
      }
    },
    onError: (error: unknown) => {
      const message = getErrorMessage(error);
      notifications.error('Receive Order Failed', message);
    },
  });
};
